import type {
    SketchDimensionArcCondition,
    SketchDimensionBranchState,
    SketchDimensionDisplayState,
    SketchDimensionKind,
    SketchDimensionPlainData,
    SketchDimensionQuantityKind,
    SketchDimensionRole,
    SketchDimensionSnapshot,
    SketchDimensionSolveState,
} from '../SketchDimensionTypes';
import {SketchDimensionReference} from './SketchDimensionReference';

/** 角度语义的核心类型集合。 */
const ANGLE_DIMENSION_KINDS: ReadonlySet<SketchDimensionKind> = new Set([
    'angle',
    'three-point-angle',
    'symmetric-angle',
]);

/** 完整圆周（弧度）。 */
const FULL_CIRCLE_RADIANS = Math.PI * 2;

/**
 * 解析核心类型对应的参数量纲。
 *
 * @param dimensionKind 尺寸核心类型。
 * @returns 参数量纲。
 */
export function resolveSketchDimensionQuantityKind(dimensionKind: SketchDimensionKind): SketchDimensionQuantityKind {
    return ANGLE_DIMENSION_KINDS.has(dimensionKind) ? 'angle' : 'length';
}

/**
 * 解析核心类型的默认业务值换算系数。
 *
 * 核心语义与显示分离（02 计划 §5.2）：直径的求解量是半径、双倍距离的求解量
 * 是单侧距离，业务值 = 求解几何量 × 系数。
 *
 * @param dimensionKind 尺寸核心类型。
 * @returns 默认换算系数。
 */
export function resolveSketchDimensionDefaultValueScale(dimensionKind: SketchDimensionKind): number {
    return dimensionKind === 'diameter' || dimensionKind === 'doubled-distance' ? 2 : 1;
}

/**
 * 校验尺寸名义值的合法值域。
 *
 * 说明：零/负值方向翻转是 V 级待实机行为（01 基线 §9.2），当前保守拒绝非正值；
 * 实机金标后如放宽，只需调整本函数。
 *
 * @param quantityKind 参数量纲。
 * @param value 名义值（核心单位）。
 * @returns 值非法时返回错误消息；合法返回 null。
 */
export function validateSketchDimensionNominalValue(
    quantityKind: SketchDimensionQuantityKind,
    value: number,
): string | null {
    if (!Number.isFinite(value)) {
        return `尺寸名义值必须是有限数值：${String(value)}`;
    }
    if (value <= 0) {
        return `尺寸名义值必须为正（零/负值翻转行为待实机金标）：${value}`;
    }
    if (quantityKind === 'angle' && value >= FULL_CIRCLE_RADIANS) {
        return `角度尺寸名义值必须小于 2π 弧度：${value}`;
    }

    return null;
}

/**
 * 草图尺寸聚合。
 *
 * 设计说明：
 * 1. 聚合持有完整领域主状态并守卫不变量；所有变更走受控方法，禁止外部直接改字段；
 * 2. `toSnapshot()/fromSnapshot()` 输出/接收纯数据快照，供历史 adapter 与持久化直接消费；
 * 3. 聚合不持有 Three.js 对象、求解器或渲染资源；求解结果只通过 `markSolveState` 写回；
 * 4. readOnly 尺寸拒绝一切用户发起的修改（重命名/改值/角色/引用/显示），
 *    但允许系统写回求解状态与测量值。
 */
export class SketchDimension {
    /** 尺寸持久 ID。 */
    public readonly dimensionId: string;

    /** 所属草图持久 ID。 */
    public readonly sketchId: string;

    /** 核心类型。 */
    public readonly dimensionKind: SketchDimensionKind;

    /** 空间语义。 */
    public readonly space: SketchDimensionSnapshot['space'];

    /** 尺寸名称。 */
    private name: string;

    /** 驱动角色。 */
    private role: SketchDimensionRole;

    /** 是否被抑制。 */
    private suppressed: boolean;

    /** 是否只读。 */
    private readonly readOnly: boolean;

    /** 参数绑定。 */
    private parameter: SketchDimensionSnapshot['parameter'];

    /** driven 测量值。 */
    private measuredValue: number | null;

    /** 有序引用。 */
    private references: SketchDimensionReference[];

    /** 分支状态。 */
    private branch: SketchDimensionBranchState;

    /** 显示状态。 */
    private display: SketchDimensionDisplayState;

    /** 求解状态。 */
    private solveState: SketchDimensionSolveState;

    /** 悬空修复状态。 */
    private repair: SketchDimensionSnapshot['repair'];

    /** 扩展元数据（等距链联动等派生载荷）；无派生数据为 null。 */
    private metadata: SketchDimensionSnapshot['metadata'] | null;

    /**
     * @param snapshot 尺寸完整快照。
     */
    private constructor(snapshot: SketchDimensionSnapshot) {
        if (!snapshot.dimensionId) {
            throw new Error('尺寸快照缺少 dimensionId。');
        }
        if (!snapshot.sketchId) {
            throw new Error(`尺寸 ${snapshot.dimensionId} 快照缺少 sketchId。`);
        }
        if (!snapshot.name) {
            throw new Error(`尺寸 ${snapshot.dimensionId} 快照缺少名称。`);
        }

        const expectedQuantityKind = resolveSketchDimensionQuantityKind(snapshot.dimensionKind);

        if (snapshot.parameter.quantityKind !== expectedQuantityKind) {
            throw new Error(
                `尺寸 ${snapshot.dimensionId} 参数量纲 ${snapshot.parameter.quantityKind} `
                + `与核心类型 ${snapshot.dimensionKind} 不匹配（应为 ${expectedQuantityKind}）。`,
            );
        }

        const valueError = validateSketchDimensionNominalValue(
            snapshot.parameter.quantityKind,
            snapshot.parameter.nominalValue,
        );

        if (valueError) {
            throw new Error(`尺寸 ${snapshot.dimensionId} 快照非法：${valueError}`);
        }

        const references = snapshot.references.map((record) => new SketchDimensionReference(record));
        const sortedBySelection = [...references]
            .sort((left, right) => left.selectionOrder - right.selectionOrder);
        const hasDuplicateOrder = new Set(references.map((reference) => reference.selectionOrder)).size
            !== references.length;

        if (references.length === 0) {
            throw new Error(`尺寸 ${snapshot.dimensionId} 必须至少有一个引用。`);
        }
        if (hasDuplicateOrder) {
            throw new Error(`尺寸 ${snapshot.dimensionId} 引用选择顺序重复。`);
        }

        this.dimensionId = snapshot.dimensionId;
        this.sketchId = snapshot.sketchId;
        this.dimensionKind = snapshot.dimensionKind;
        this.space = snapshot.space;
        this.name = snapshot.name;
        this.role = snapshot.role;
        this.suppressed = snapshot.suppressed;
        this.readOnly = snapshot.readOnly;
        this.parameter = {...snapshot.parameter};
        this.measuredValue = snapshot.measuredValue;
        this.references = sortedBySelection;
        this.branch = {...snapshot.branch};
        this.display = {...snapshot.display};
        this.solveState = snapshot.solveState;
        this.repair = snapshot.repair ? {...snapshot.repair} : null;
        this.metadata = snapshot.metadata
            ? {
                offsetChain: snapshot.metadata.offsetChain
                    ? {
                        sourceEntityIds: [...snapshot.metadata.offsetChain.sourceEntityIds],
                        offsetEntityIds: [...snapshot.metadata.offsetChain.offsetEntityIds],
                        globalIsReverse: snapshot.metadata.offsetChain.globalIsReverse,
                    }
                    : undefined,
            }
            : null;
    }

    /**
     * 从纯数据快照重建聚合。
     *
     * @param snapshot 尺寸完整快照。
     * @returns 尺寸聚合。
     */
    public static fromSnapshot(snapshot: SketchDimensionSnapshot): SketchDimension {
        return new SketchDimension(snapshot);
    }

    /**
     * 输出纯数据快照。
     *
     * @returns 尺寸完整快照（深拷贝，修改返回值不影响聚合）。
     */
    public toSnapshot(): SketchDimensionSnapshot {
        return {
            dimensionId: this.dimensionId,
            sketchId: this.sketchId,
            name: this.name,
            dimensionKind: this.dimensionKind,
            space: this.space,
            role: this.role,
            suppressed: this.suppressed,
            readOnly: this.readOnly,
            parameter: {...this.parameter},
            measuredValue: this.measuredValue,
            references: this.references.map((reference) => reference.toRecord()),
            branch: {...this.branch},
            display: {...this.display},
            solveState: this.solveState,
            repair: this.repair ? {...this.repair} : null,
            // 无元数据时省略字段，保证与旧快照（无该字段）的序列化形态一致
            ...(this.metadata ? {
                metadata: {
                    offsetChain: this.metadata.offsetChain
                        ? {
                            sourceEntityIds: [...this.metadata.offsetChain.sourceEntityIds],
                            offsetEntityIds: [...this.metadata.offsetChain.offsetEntityIds],
                            globalIsReverse: this.metadata.offsetChain.globalIsReverse,
                        }
                        : undefined,
                },
            } : {}),
        };
    }

    /** 当前名称。 */
    public get currentName(): string {
        return this.name;
    }

    /** 当前驱动角色。 */
    public get currentRole(): SketchDimensionRole {
        return this.role;
    }

    /** 当前名义值（核心单位）。 */
    public get nominalValue(): number {
        return this.parameter.nominalValue;
    }

    /** 参数稳定 ID。 */
    public get parameterId(): string {
        return this.parameter.parameterId;
    }

    /** 当前求解状态。 */
    public get currentSolveState(): SketchDimensionSolveState {
        return this.solveState;
    }

    /** 是否被抑制。 */
    public get isSuppressed(): boolean {
        return this.suppressed;
    }

    /** 是否悬空。 */
    public get isDangling(): boolean {
        return this.solveState === 'dangling';
    }

    /** 当前扩展元数据（等距链联动等派生载荷）；无派生数据为 null。 */
    public get currentMetadata(): SketchDimensionSnapshot['metadata'] | null {
        return this.metadata;
    }

    /** 当前有序引用（副本）。 */
    public get currentReferences(): readonly SketchDimensionReference[] {
        return [...this.references];
    }

    /** 断言尺寸可被用户修改。 */
    private assertWritable(action: string): void {
        if (this.readOnly) {
            throw new Error(`尺寸 ${this.dimensionId} 为只读，拒绝${action}。`);
        }
    }

    /**
     * 重命名。
     *
     * @param name 新名称（草图内唯一性由仓库校验）。
     * @throws 当尺寸为只读，或名称去除空白后为空时抛出错误。
     */
    public rename(name: string): void {
        this.assertWritable('重命名');
        if (!name.trim()) {
            throw new Error('尺寸名称不能为空。');
        }
        this.name = name.trim();
    }

    /**
     * 修改 driving 名义值。
     *
     * @param nominalValue 新名义值（核心单位：米/弧度）。
     * @throws 当尺寸为只读或是 driven（参考）尺寸时抛出错误。
     * @throws 当名义值不符合当前量纲的取值规则时抛出错误。
     */
    public changeNominalValue(nominalValue: number): void {
        this.assertWritable('修改名义值');
        if (this.role !== 'driving') {
            throw new Error(`尺寸 ${this.dimensionId} 是 driven（参考）尺寸，不能直接写名义值。`);
        }

        const valueError = validateSketchDimensionNominalValue(this.parameter.quantityKind, nominalValue);

        if (valueError) {
            throw new Error(`尺寸 ${this.dimensionId} 改值被拒绝：${valueError}`);
        }
        this.parameter = {...this.parameter, nominalValue};
    }

    /**
     * 切换驱动角色。
     *
     * 说明：driven→driving 的过定义判定属于求解器（阶段 3）；本方法只负责角色
     * 主状态切换，切换后求解状态回到 unknown 等待下一次求解写回。
     *
     * @param role 目标角色。
     */
    public changeRole(role: SketchDimensionRole): void {
        this.assertWritable('切换驱动角色');
        if (this.role === role) {
            return;
        }
        this.role = role;
        this.solveState = 'unknown';
    }

    /**
     * 修改指定引用的圆系附着条件。
     *
     * @param selectionOrder 目标引用的选择顺序。
     * @param arcCondition 新附着条件。
     * @throws 当尺寸为只读、指定顺序的引用不存在，或该引用不是圆系引用时抛出错误。
     */
    public changeArcCondition(selectionOrder: number, arcCondition: SketchDimensionArcCondition): void {
        this.assertWritable('修改附着条件');

        const index = this.references.findIndex((reference) => reference.selectionOrder === selectionOrder);

        if (index < 0) {
            throw new Error(`尺寸 ${this.dimensionId} 不存在选择顺序为 ${selectionOrder} 的引用。`);
        }
        if (this.references[index].arcCondition === undefined) {
            throw new Error(`尺寸 ${this.dimensionId} 引用 ${selectionOrder} 不是圆系引用，不能设置附着条件。`);
        }
        this.references[index] = this.references[index].withArcCondition(arcCondition);
        this.solveState = 'unknown';
    }

    /**
     * 更新分支状态。
     *
     * @param branch 新分支状态（整体替换）。
     */
    public changeBranch(branch: SketchDimensionBranchState): void {
        this.assertWritable('修改分支');
        this.branch = {...branch};
    }

    /**
     * 更新显示状态（浅合并）。
     *
     * @param display 待合并的显示字段。
     */
    public updateDisplay(display: SketchDimensionDisplayState): void {
        this.assertWritable('修改显示状态');
        this.display = {...this.display, ...display};
    }

    /**
     * 设置抑制状态。
     *
     * @param suppressed 是否抑制。
     */
    public setSuppressed(suppressed: boolean): void {
        this.assertWritable('修改抑制状态');
        this.suppressed = suppressed;
    }

    /**
     * 写回测量值（driven 显示值或 driving 诊断值）。
     *
     * @param measuredValue 测量值（核心单位）；清除传 null。
     * @throws 当非空测量值不是有限数值时抛出错误。
     */
    public updateMeasuredValue(measuredValue: number | null): void {
        if (measuredValue !== null && !Number.isFinite(measuredValue)) {
            throw new Error(`尺寸 ${this.dimensionId} 测量值必须是有限数值：${String(measuredValue)}`);
        }
        this.measuredValue = measuredValue;
    }

    /**
     * 写回求解状态（系统通道，readOnly 尺寸也允许）。
     *
     * @param solveState 求解状态。
     * @throws 当试图直接写入 `dangling` 状态而未通过 `markDangling` 保留修复信息时抛出错误。
     */
    public markSolveState(solveState: SketchDimensionSolveState): void {
        if (solveState === 'dangling') {
            throw new Error('悬空状态必须通过 markDangling 写入以保留修复信息。');
        }
        this.solveState = solveState;
        if (solveState !== 'unknown') {
            this.repair = null;
        }
    }

    /**
     * 标记悬空并保留修复信息。
     *
     * @param ghostData ghost 显示数据；无数据可省略。
     */
    public markDangling(ghostData?: SketchDimensionPlainData): void {
        this.solveState = 'dangling';
        this.repair = {
            originalReferenceSignature: SketchDimensionReference.combinedSignature(this.references),
            ...(ghostData === undefined ? {} : {ghostData}),
        };
    }

    /**
     * 重新附着引用。
     *
     * 保留 dimensionId/名称/参数身份，替换引用并清除悬空修复状态。
     *
     * @param references 新引用列表。
     * @throws 当尺寸为只读，或尺寸类型为不支持重新附着的路径长度或样条长度时抛出错误。
     * @throws 当新引用列表为空或包含重复选择顺序时抛出错误。
     */
    public reattach(references: readonly SketchDimensionReference[]): void {
        this.assertWritable('重新附着');
        // Reattach 排除清单（DANG-003，01 基线 §11.3）：链/总长类尺寸不支持重新附着。
        if (this.dimensionKind === 'path-length' || this.dimensionKind === 'spline-length') {
            throw new Error(`尺寸 ${this.dimensionId} 类型 ${this.dimensionKind} 不支持重新附着（Reattach 排除清单）。`);
        }
        if (references.length === 0) {
            throw new Error(`尺寸 ${this.dimensionId} 重新附着必须提供至少一个引用。`);
        }

        const hasDuplicateOrder = new Set(references.map((reference) => reference.selectionOrder)).size
            !== references.length;

        if (hasDuplicateOrder) {
            throw new Error(`尺寸 ${this.dimensionId} 重新附着引用选择顺序重复。`);
        }
        this.references = [...references].sort((left, right) => left.selectionOrder - right.selectionOrder);
        this.repair = null;
        this.solveState = 'unknown';
    }
}
